// UI state outside the engine: the stored options (with persistence), the window stack, the menu,
// loading/startup flags and the logged-in player. A small external store read with
// useSyncExternalStore. Option side effects (engine, sound, body background) are wired by app-entry.
import { useSyncExternalStore } from 'react';
import type { GameOptionValues, SkinValues, StoredOptions } from '../options/storage';
import { writeStoredOptions } from '../options/storage';

export type WindowName =
  | 'games'
  | 'options'
  | 'opt_skin'
  | 'pause'
  /** Mahjong: no move left (ui/mahjongHelp.ts). */
  | 'no_moves'
  /** Kierki: the end of a deal or of the game, with the score table (hearts/HeartsTable.tsx). */
  | 'hearts_score'
  /** Makao: the end of the deal (makao/MakaoTable.tsx). */
  | 'makao_end'
  | 't58_score'
  | 't1k_score'
  /** Kulki: the end of the game, the board full (kulki/KulkiTable.tsx). */
  | 'kulki_end'
  | 'info'
  | 'confirm'
  | 'alert'
  | 'win'
  | 'auth'
  | 'auth_dashboard'
  /** Panel: a request to delete the account (AccountWindows.tsx DeleteAccountWindow). */
  | 'delete_account'
  | 'flags'
  | 'ranking'
  | 'multiplayer'
  | 'multiplayer_win'
  /** Multiplayer: the Leave button's choice (I'm done / Leave / Keep playing), and the wait after "I'm done". */
  | 'multiplayer_leave'
  | 'multiplayer_done';

export interface WindowInstance {
  id: number;
  name: WindowName;
  props: Record<string, unknown>;
  /** Set while the 400 ms close animation runs; the element is removed afterwards. */
  closing: boolean;
  /** Hidden but alive (the rooms window keeps its connection while a game runs). */
  minimized: boolean;
}

export interface Player {
  player_id: string;
  player_name: string;
  country_name: string | null;
  country_code: string;
  flag_src: string;
  login_time?: string;
  signup_time?: string;
}

export interface UiState {
  options: StoredOptions;
  windows: WindowInstance[];
  menuOpen: boolean;
  loading: boolean;
  startupDone: boolean;
  player: Player | null;
  /** Fullscreen state for the footer icon. */
  fullscreen: boolean;
}

type OptionKey = keyof GameOptionValues;

export interface UiStore {
  getState(): UiState;
  subscribe(fn: () => void): () => void;
  set(patch: Partial<UiState>): void;
  /** Change one option, persist, and run the side effect hook. */
  setOption<K extends OptionKey>(key: K, value: GameOptionValues[K]): void;
  setSkin(patch: Partial<SkinValues>): void;
  save(): void;
  openWindow(name: WindowName, props?: Record<string, unknown>): void;
  closeWindow(name: WindowName): void;
  toggleWindow(name: WindowName, props?: Record<string, unknown>): void;
  minimizeWindow(name: WindowName): void;
  restoreWindow(name: WindowName): void;
  isOpen(name: WindowName): boolean;
  /** Called after an option changed (set by app-entry). */
  onOptionChange: ((key: OptionKey, value: unknown, options: StoredOptions) => void) | null;
  onSkinChange: ((skin: SkinValues) => void) | null;
  onWindowClose: ((name: WindowName) => void) | null;
}

export const WINDOW_CLOSE_MS = 400;

export function createUiStore(initial: StoredOptions): UiStore {
  let state: UiState = {
    options: initial,
    windows: [],
    menuOpen: false,
    loading: true,
    startupDone: false,
    player: null,
    fullscreen: false,
  };
  const listeners = new Set<() => void>();
  let nextId = 1;
  const closeTimers = new Map<number, number>();
  const notify = () => {
    for (const fn of listeners) fn();
  };
  const set = (patch: Partial<UiState>) => {
    state = { ...state, ...patch };
    notify();
  };
  const store: UiStore = {
    getState: () => state,
    subscribe(fn) {
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
      };
    },
    set,
    setOption(key, value) {
      const options = { ...state.options, option: { ...state.options.option, [key]: value } };
      set({ options });
      writeStoredOptions(options);
      store.onOptionChange?.(key, value, options);
    },
    setSkin(patch) {
      const options = { ...state.options, skin: { ...state.options.skin, ...patch } };
      set({ options });
      writeStoredOptions(options);
      store.onSkinChange?.(options.skin);
    },
    save: () => writeStoredOptions(state.options),
    openWindow(name, props = {}) {
      const existing = state.windows.find((w) => w.name === name);
      if (existing) {
        // Re-opening within the close animation revives the same window (Q-WIN-1 fixed).
        const t = closeTimers.get(existing.id);
        if (t !== undefined) {
          clearTimeout(t);
          closeTimers.delete(existing.id);
        }
        set({
          windows: state.windows.map((w) =>
            w.id === existing.id ? { ...w, props, closing: false, minimized: false } : w,
          ),
        });
        return;
      }
      set({ windows: [...state.windows, { id: nextId++, name, props, closing: false, minimized: false }] });
    },
    closeWindow(name) {
      const w = state.windows.find((x) => x.name === name && !x.closing);
      if (!w) return;
      set({ windows: state.windows.map((x) => (x.id === w.id ? { ...x, closing: true } : x)) });
      const timer = window.setTimeout(() => {
        closeTimers.delete(w.id);
        set({ windows: state.windows.filter((x) => x.id !== w.id) });
        store.onWindowClose?.(name);
      }, WINDOW_CLOSE_MS);
      closeTimers.set(w.id, timer);
    },
    toggleWindow(name, props) {
      const w = state.windows.find((x) => x.name === name && !x.closing);
      if (w && !w.minimized) store.closeWindow(name);
      else store.openWindow(name, props);
    },
    minimizeWindow(name) {
      set({ windows: state.windows.map((w) => (w.name === name ? { ...w, minimized: true } : w)) });
    },
    restoreWindow(name) {
      set({ windows: state.windows.map((w) => (w.name === name ? { ...w, minimized: false } : w)) });
    },
    isOpen: (name) => state.windows.some((w) => w.name === name && !w.closing),
    onOptionChange: null,
    onSkinChange: null,
    onWindowClose: null,
  };
  return store;
}

let current: UiStore | null = null;
export const setUiStore = (s: UiStore): void => {
  current = s;
};
export const uiStore = (): UiStore => {
  if (!current) throw new Error('uiStore not created');
  return current;
};
export function useUiState(): UiState {
  const s = uiStore();
  return useSyncExternalStore(s.subscribe, s.getState, s.getState);
}
